/**
 * Copy text to the clipboard, working on a plain-HTTP tenant subdomain (`acme.lvh.me:5173` in
 * local dev, and any non-TLS deployment), not just `localhost`.
 *
 * `navigator.clipboard` only exists in a "secure context" — HTTPS, or the literal hostname
 * `localhost`. A browser does NOT extend that to other hostnames that happen to resolve to
 * 127.0.0.1 (`lvh.me`, `/etc/hosts` entries, ...), so on this app's own subdomain-per-tenant
 * origin (ADR 0007) the API is simply `undefined` — calling it throws a TypeError, not a
 * permission error, and every previous call site here either dropped that promise unawaited or
 * swallowed the throw and still reported success.
 *
 * `document.execCommand('copy')` has no such restriction — it's deprecated but still implemented
 * everywhere that matters, and is exactly the fallback MDN itself documents for this gap. Resolves
 * `true`/`false` so a caller can show an honest result instead of assuming success.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the legacy path below — a permission prompt denial, most likely.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Off-screen, not display:none — an invisible element can't receive focus/selection in Safari.
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  let succeeded = false;
  try {
    succeeded = document.execCommand('copy');
  } catch {
    succeeded = false;
  } finally {
    document.body.removeChild(textarea);
  }
  return succeeded;
}
